import React from "react";

type SheetProps = {
    open: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    /** Label for the dismiss button. "Done" for settings, "Cancel" where nothing was applied. */
    closeLabel?: string;
    children: React.ReactNode;
};

/**
 * Bottom sheet.
 *
 * Kept mounted and translated off-screen rather than unmounted, so it animates in and out instead of
 * appearing instantly — but with `pointer-events` and `aria-hidden` following the open state, so a
 * closed sheet is neither tappable nor reachable by a screen reader.
 */
export const Sheet: React.FC<SheetProps> = ({ open, onClose, title, subtitle, closeLabel = "Done", children }) => (
    <div
        className={`fixed inset-0 z-20 ${open ? "" : "pointer-events-none"}`}
        aria-hidden={!open}
        role="dialog"
        aria-label={title}
    >
        <button
            type="button"
            aria-label="Close"
            tabIndex={open ? 0 : -1}
            onClick={onClose}
            className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
        />
        <div
            /* mouse:max-w matches CabFrame's phone-width column: the sheet is fixed-position, so
               it escapes that frame and must cap itself or it spans the whole monitor. Gated the
               same way as the frame — hover devices only — so a mounted iPad keeps the phone's
               edge-to-edge sheet. The scrim behind it stays viewport-wide everywhere. */
            className={`absolute inset-x-0 bottom-0 mx-auto max-h-[88%] w-full overflow-y-auto rounded-t-3xl border-t border-[var(--line-2)] bg-[var(--surface)] transition-transform duration-[260ms] mouse:max-w-[520px] mouse:min-[521px]:border-x ${
                open ? "translate-y-0" : "translate-y-full"
            }`}
            style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
            <div className="mx-auto mt-2.5 h-1 w-9 rounded-sm bg-[var(--line-2)]" />
            <div className="flex items-start justify-between gap-3 px-4 pb-3 pt-2">
                <div>
                    <div className="text-[16px] font-bold tracking-tight">{title}</div>
                    {subtitle && <div className="mt-0.5 text-[11.5px] text-[var(--muted)]">{subtitle}</div>}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    tabIndex={open ? 0 : -1}
                    className="min-h-11 flex-none rounded-[9px] bg-[var(--surface-2)] px-3 text-[12.5px] font-semibold text-[var(--muted)]"
                >
                    {closeLabel}
                </button>
            </div>
            <div className="flex flex-col gap-2.5 px-4">{children}</div>
        </div>
    </div>
);
